import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { usePageContextQuery } from "@/shared/page-context";
import { getUserPreferences } from "./getUserPreferences";
import { saveSearchPreferences } from "./saveSearchPreferences";
import type { SaveUserPreferencesPayload } from "./types";

export const userPreferencesQueryKey = ["user-preferences"];

export const useUserPreferencesQuery = () => {
    const { data: pageContext } = usePageContextQuery();

    return useSuspenseQuery({
        queryKey: userPreferencesQueryKey,
        queryFn: () => {
            // useSuspenseQuery does not accept "enabled" param,
            // so manually make a check here
            if (pageContext.isInstitutional) return null;
            return getUserPreferences(pageContext.partnerId).catch(() => null);
        },
    });
};

export const useSaveUserPreferencesMutation = () => {
    const { data: pageContext } = usePageContextQuery();

    const queryClient = useQueryClient();

    // Prevent saving data for institutional users
    return useMutation({
        mutationFn: (data: SaveUserPreferencesPayload) => {
            if (pageContext.isInstitutional) return Promise.resolve();
            return saveSearchPreferences(pageContext.partnerId, data);
        },
        onSuccess: () => {
            // If user is institutional - no mutation was executed
            // So there's no need to invalidate the queries
            if (!pageContext.isInstitutional) {
                queryClient.invalidateQueries({ queryKey: userPreferencesQueryKey });
            }
        },
    });
};
